import { Input, MenuItem, Select, SelectChangeEvent, Typography } from '@mui/material';
import type { GridRenderCellParams, GridRowId, GridValidRowModel } from '@mui/x-data-grid';
import { Trans } from 'react-i18next';
import { formatDate, formatDateWithTime } from '../../utils/format';
import { GridCellExpand } from './GridCellExpand';

export const renderShowCellOrDefault = (show: boolean, params: GridRenderCellParams) => {
  if (params.value && show) {
    return;
  }

  return (
    <Typography color={'text.disabled'} variant={'caption'}>
      <Trans i18nKey={'notSet'} />
    </Typography>
  );
};

export const renderCellOrDefault = (params: GridRenderCellParams) => {
  if (params.value) {
    return;
  }

  return (
    <Typography color={'text.disabled'} variant={'caption'}>
      <Trans i18nKey={'notSet'} />
    </Typography>
  );
};

export const renderNumberOrDefault = (params: GridRenderCellParams) => {
  if (params.value || params.value === 0) {
    return;
  }

  return (
    <Typography color={'text.disabled'} variant={'caption'}>
      <Trans i18nKey={'notSet'} />
    </Typography>
  );
};

export const renderDateOrDefault = (params: GridRenderCellParams) => {
  if (params.value) {
    const date = new Date(params.value);
    return formatDate(date);
  }

  return (
    <Typography color={'text.disabled'} variant={'caption'}>
      <Trans i18nKey={'notSet'} />
    </Typography>
  );
};

export function renderDateWithTimeOrDefault(params: GridRenderCellParams) {
  if (params.value) {
    const date = new Date(params.value);
    return formatDateWithTime(date);
  }

  return (
    <Typography color={'text.disabled'} variant={'caption'}>
      <Trans i18nKey={'notSet'} />
    </Typography>
  );
}

export function renderCellExpand(params: GridRenderCellParams<GridValidRowModel, string>) {
  return <GridCellExpand value={params.value || ''} width={params.colDef.computedWidth} />;
}

type Item<T extends string> = {
  value: T;
  label: React.ReactNode;
};

export function renderCellSelect<T extends string>(
  params: GridRenderCellParams<GridValidRowModel, T>,
  options: Item<T>[],
  onChange: (rowId: GridRowId, value: T) => void,
  isUpdating: boolean,
) {
  const handleChange = (e: SelectChangeEvent<string>) => {
    onChange(params.id, e.target.value as T);
  };

  return (
    <Select<string>
      size="small"
      variant="standard"
      value={params.value ?? ''}
      fullWidth
      onChange={handleChange}
      disabled={isUpdating}
      input={<Input disableUnderline />}
      sx={(theme) => ({
        '& .MuiSelect-select.Mui-disabled': {
          color: theme.palette.text.primary,
          WebkitTextFillColor: theme.palette.text.primary,
        },
        '&.Mui-disabled .MuiSelect-icon': {
          display: 'none',
        },
      })}
    >
      {options.map((item) => (
        <MenuItem key={item.value} value={item.value}>
          {item.label}
        </MenuItem>
      ))}
    </Select>
  );
}
